import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown } from 'lucide-react'
import {
    boxTruckDatSelection,
    boxTruckDatSelectionState,
    type BoxTruckDatSubtypeInteraction,
    equipmentLabel,
    filterBoxTruckDatGroups,
    isBoxTruckDatPickerOpenByDefault,
    toggleBoxTruckDatCode,
    toggleBoxTruckDatGroup,
} from '@numeo/types-core'
import { Checkbox, SearchField } from '@/components/ds2'

interface BoxTruckDatEquipmentSectionProps {
    equipmentLabels: string[]
    selectedCodes: string[]
    onSelectedCodesChange: (codes: string[], interaction: BoxTruckDatSubtypeInteraction) => void
}

/** Exact DAT subtype selection, shown inline only while the DAT source is enabled. */
export const BoxTruckDatEquipmentSection = ({ equipmentLabels, selectedCodes, onSelectedCodesChange }: BoxTruckDatEquipmentSectionProps) => {
    const [expanded, setExpanded] = useState(() => isBoxTruckDatPickerOpenByDefault(equipmentLabels, selectedCodes))
    const [query, setQuery] = useState('')
    const selected = useMemo(() => new Set(selectedCodes), [selectedCodes])
    // Counted by the same helper the request builder and the badges use, so the Amplitude `selection` state
    // cannot say "all" for a selection the search treats as narrowed.
    const { active: activeCodes, requested } = useMemo(() => boxTruckDatSelection(equipmentLabels, selectedCodes), [equipmentLabels, selectedCodes])
    const selectedCount = requested.length
    const selectionLabel =
        selectedCount === 0
            ? 'No types selected'
            : selectedCount === activeCodes.length
              ? `All ${activeCodes.length} types`
              : `${selectedCount} of ${activeCodes.length} types`
    const hasQuery = query.trim().length > 0
    const filteredGroups = useMemo(() => filterBoxTruckDatGroups(equipmentLabels, query), [equipmentLabels, query])

    const toggleCode = (groupId: BoxTruckDatSubtypeInteraction['groupId'], code: string) => {
        const { codes, action } = toggleBoxTruckDatCode(selectedCodes, code)
        onSelectedCodesChange(codes, { groupId, action, selection: boxTruckDatSelectionState(equipmentLabels, codes) })
    }

    const toggleGroup = (groupId: BoxTruckDatSubtypeInteraction['groupId'], groupCodes: readonly string[]) => {
        const { codes, action } = toggleBoxTruckDatGroup(selectedCodes, groupCodes)
        onSelectedCodesChange(codes, { groupId, action, selection: boxTruckDatSelectionState(equipmentLabels, codes) })
    }

    return (
        <div className="overflow-hidden rounded-xl bg-surface shadow-field">
            <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setExpanded((current) => !current)}
                className="flex min-h-9 w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <span>
                    DAT equipment <span className="ml-1 font-normal text-foreground-muted">{selectionLabel}</span>
                </span>
                <ChevronDown className={`size-4 shrink-0 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {expanded && (
                <div className="border-t border-border px-3 pb-3 pt-2">
                    <p className="mb-2 text-xs text-foreground-muted">Choose the DAT types for your selected equipment.</p>
                    {selectedCount === 0 && (
                        <p role="status" className="mb-2 flex items-start gap-1.5 text-xs leading-4 text-warning">
                            <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                            Select at least one type to include DAT loads in Search.
                        </p>
                    )}
                    <SearchField
                        variant="secondary"
                        showLabel={false}
                        showDescription={false}
                        placeholder="Find code or type…"
                        value={query}
                        onChange={setQuery}
                    />

                    <div className="mt-2 flex flex-col">
                        {filteredGroups.map(({ group, shownCodes }) => {
                            const groupSelectedCount = shownCodes.filter((code) => selected.has(code)).length
                            const allShownCodesSelected = groupSelectedCount === shownCodes.length
                            return (
                                <section key={group.id} className="border-b border-border py-3 last:border-0 last:pb-0">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <div>
                                            <h4 className="text-sm font-semibold text-foreground">{group.title}</h4>
                                            <p className="text-xs text-foreground-muted">
                                                Up to {group.weightLbs.toLocaleString()} lb · {group.lengthFt} ft
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(group.id, shownCodes)}
                                            className="shrink-0 rounded-3xl px-2 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            {groupSelectedCount}/{shownCodes.length} ·{' '}
                                            {allShownCodesSelected ? (hasQuery ? 'Clear shown' : 'Clear') : hasQuery ? 'Select all shown' : 'Select all'}
                                        </button>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {shownCodes.map((code) => (
                                            <div key={code} className="rounded-xl bg-surface-secondary px-3 py-2">
                                                <Checkbox
                                                    checked={selected.has(code)}
                                                    onChange={() => toggleCode(group.id, code)}
                                                    title={`${code} — ${equipmentLabel(code)}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )
                        })}
                        {filteredGroups.length === 0 && <p className="py-3 text-sm text-foreground-muted">No matching types.</p>}
                    </div>
                </div>
            )}
        </div>
    )
}
